import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  ModalHeader,
  ModalContent,
  ModalActions,
  TextField,
  Button,
  Tabs,
  Tab,
  TabPanel,
  IconButton,
  Select,
  H4,
  H6
} from './ui';
import { taskService } from '../services/taskService';
import { aiService } from '../services/aiService';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

function JobDetailsModal({ open, onClose, onSubmit, job = null, defaultStage = 'wishlist' }) {
  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState({
    jobTitle: '',
    company: '',
    jobUrl: '',
    location: '',
    notes: '',
    stage: defaultStage
  });
  const [loading, setLoading] = useState(false);
  const [savingFields, setSavingFields] = useState({}); // Track which fields are saving
  const [editingTitle, setEditingTitle] = useState(false); // Track if title is being edited
  const [editingCompany, setEditingCompany] = useState(false); // Track if company is being edited
  
  // Task-related state
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskFormData, setTaskFormData] = useState({
    title: '',
    dueDate: ''
  });
  const [taskCreating, setTaskCreating] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskData, setEditingTaskData] = useState({ title: '', dueDate: '' });
  const [taskMenuOpen, setTaskMenuOpen] = useState(null); // Store task ID for which menu is open
  const [editingDateTaskId, setEditingDateTaskId] = useState(null);

  // AI Analysis state
  const [aiAnalysisCache, setAiAnalysisCache] = useState({}); // Store analysis per job ID
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  
  // Get current job's analysis from cache
  const aiAnalysis = job?._id ? aiAnalysisCache[job._id] : null;

  // Calendar picker state
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [editingFormDate, setEditingFormDate] = useState(false); // For task creation form
  const [editingInlineDate, setEditingInlineDate] = useState(false); // For inline editing
  const calendarRef = useRef(null);
  const formCalendarRef = useRef(null);
  const inlineCalendarRef = useRef(null);

  const isEditing = Boolean(job);

  // Month names for the grid
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  // Handle month selection
  const handleMonthSelect = (monthIndex) => {
    setSelectedMonth(monthIndex);
    setShowMonthPicker(false);
  };

  // Custom month picker component
  const MonthPicker = ({ year, onMonthSelect, onYearChange }) => (
    <div className="p-2">
      {/* Year header with navigation */}
      <div className="flex items-center justify-between mb-4">
        <button 
          onClick={() => onYearChange(year - 1)}
          className="p-1 hover:bg-purple-600 rounded"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h3 className="text-lg font-medium text-white">{year}</h3>
        <button 
          onClick={() => onYearChange(year + 1)}
          className="p-1 hover:bg-purple-600 rounded"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      
      {/* 4x3 Month grid */}
      <div className="grid grid-cols-3 gap-2">
        {monthNames.map((monthName, index) => (
          <button
            key={index}
            onClick={() => onMonthSelect(index)}
            className="p-2 text-sm text-white hover:bg-purple-600 rounded transition-colors"
          >
            {monthName}
          </button>
        ))}
      </div>
    </div>
  );

  // Click outside to close calendar
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setEditingDateTaskId(null);
      }
      if (formCalendarRef.current && !formCalendarRef.current.contains(event.target)) {
        setEditingFormDate(false);
      }
      if (inlineCalendarRef.current && !inlineCalendarRef.current.contains(event.target)) {
        setEditingInlineDate(false);
      }
    };

    if (editingDateTaskId || editingFormDate || editingInlineDate) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingDateTaskId, editingFormDate, editingInlineDate]);

  // Debounced auto-save function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    (() => {
      let timeoutId;
      return (fieldName, value, jobId) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          if (!jobId || !isEditing) return;
          
          try {
            setSavingFields(prev => ({ ...prev, [fieldName]: true }));
            const updateData = { [fieldName]: value };
            await onSubmit(jobId, updateData);
            
            // Brief delay to show the saving state
            setTimeout(() => {
              setSavingFields(prev => ({ ...prev, [fieldName]: false }));
            }, 300);
          } catch (error) {
            console.error('Error auto-saving field:', error);
            setSavingFields(prev => ({ ...prev, [fieldName]: false }));
          }
        }, 800); // 800ms delay after user stops typing
      };
    })(),
    [onSubmit, isEditing]
  );

  // Update form data when job changes or modal opens
  useEffect(() => {
    if (open) {
      if (job) {
        // Editing existing job
        setFormData({
          jobTitle: job.jobTitle || '',
          company: job.company || '',
          jobUrl: job.jobUrl || '',
          location: job.location || '',
          notes: job.notes || '',
          stage: job.stage || 'wishlist'
        });
      } else {
        // Creating new job - always reset form and modal state
        setFormData({
          jobTitle: '',
          company: '',
          jobUrl: '',
          location: '',
          notes: '',
          stage: defaultStage
        });
        // Reset modal state for new job
        setActiveTab(0);
        setEditingTitle(false);
        setEditingCompany(false);
        setShowTaskForm(false);
        setAiError(null);
      }
    }
    
    // AI analysis now persists per job in cache - no need to clear on job change
  }, [job, defaultStage, open]);

  const loadTasks = useCallback(async () => {
    if (!job?._id) return;
    
    try {
      setTasksLoading(true);
      const tasksData = await taskService.getTasksByJob(job._id);
      setTasks(tasksData);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setTasksLoading(false);
    }
  }, [job?._id]);

  // Load tasks when job changes and Activities tab is active
  useEffect(() => {
    if (job && activeTab === 1) {
      loadTasks();
    }
  }, [job, activeTab, loadTasks]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setShowTaskForm(false); // Close task form when switching tabs
  };

  // Handle AI Analysis
  const handleAIAnalysis = async () => {
    if (!job?._id) return;
    
    try {
      setAiLoading(true);
      setAiError(null);
      // Clear previous results for this specific job
      setAiAnalysisCache(prev => ({ ...prev, [job._id]: null }));
      const analysis = await aiService.analyzeCVRecommendation(job._id);
      // Store analysis in cache for this job ID
      setAiAnalysisCache(prev => ({ ...prev, [job._id]: analysis }));
    } catch (error) {
      console.error('Error performing AI analysis:', error);
      setAiError(error.message);
    } finally {
      setAiLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-save for existing jobs
    if (isEditing && job?._id) {
      debouncedSave(name, value, job._id);
    }
  };

  // Handle Enter key to blur/unfocus fields
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  // Handle title editing
  const handleTitleClick = () => {
    if (!editingTitle) {
      setEditingTitle(true);
    }
  };

  const handleTitleBlur = () => {
    setEditingTitle(false);
  };

  const handleTitleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
      setEditingTitle(false);
    }
  };

  // Handle company editing
  const handleCompanyClick = () => {
    if (!editingCompany) {
      setEditingCompany(true);
    }
  };

  const handleCompanyBlur = () => {
    setEditingCompany(false);
  };

  const handleCompanyKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
      setEditingCompany(false);
    }
  };

  // Task handlers
  const handleTaskFormChange = (e) => {
    const { name, value } = e.target;
    setTaskFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddTaskClick = () => {
    setShowTaskForm(true);
    setTaskFormData({ title: '', dueDate: '' });
  };

  const handleCreateTask = async () => {
    if (!taskFormData.title.trim() || !job?._id) return;

    try {
      setTaskCreating(true);
      const taskData = {
        jobId: job._id,
        title: taskFormData.title.trim(),
        ...(taskFormData.dueDate && { dueDate: taskFormData.dueDate })
      };
      
      const newTask = await taskService.createTask(taskData);
      setTasks(prev => [...prev, newTask]);
      setShowTaskForm(false);
      setTaskFormData({ title: '', dueDate: '' });
    } catch (error) {
      console.error('Error creating task:', error);
    } finally {
      setTaskCreating(false);
    }
  };

  const handleCancelTask = () => {
    setShowTaskForm(false);
    setTaskFormData({ title: '', dueDate: '' });
  };

  const handleToggleTask = async (taskId, completed) => {
    try {
      const updatedTask = await taskService.toggleTaskComplete(taskId, completed);
      setTasks(prev => prev.map(task => 
        task._id === taskId ? updatedTask : task
      ));
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await taskService.deleteTask(taskId);
      setTasks(prev => prev.filter(task => task._id !== taskId));
      setTaskMenuOpen(null);
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleEditTask = (task) => {
    setEditingTaskId(task._id);
    setEditingTaskData({
      title: task.title,
      dueDate: task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : ''
    });
    setTaskMenuOpen(null);
  };

  const handleSaveTaskEdit = async () => {
    if (!editingTaskData.title.trim()) return;

    try {
      const taskData = {
        title: editingTaskData.title.trim(),
        ...(editingTaskData.dueDate && { dueDate: editingTaskData.dueDate })
      };
      
      const updatedTask = await taskService.updateTask(editingTaskId, taskData);
      setTasks(prev => prev.map(task => 
        task._id === editingTaskId ? updatedTask : task
      ));
      setEditingTaskId(null);
      setEditingTaskData({ title: '', dueDate: '' });
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleCancelTaskEdit = () => {
    setEditingTaskId(null);
    setEditingTaskData({ title: '', dueDate: '' });
  };

  const handleInlineEditChange = (e) => {
    const { name, value } = e.target;
    setEditingTaskData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChipClick = (task) => {
    if (task.dueDate) {
      const date = new Date(task.dueDate);
      setSelectedMonth(date.getMonth());
      setSelectedYear(date.getFullYear());
    }
    setEditingDateTaskId(task._id);
    setShowMonthPicker(false);
  };

  const handleDateChange = async (taskId, newDate) => {
    try {
      const updatedTask = await taskService.updateTask(taskId, { dueDate: newDate });
      setTasks(prev => prev.map(task => 
        task._id === taskId ? updatedTask : task
      ));
      setEditingDateTaskId(null);
    } catch (error) {
      console.error('Error updating due date:', error);
    }
  };

  const handleCreateJob = async () => {
    if (!formData.jobTitle.trim() || !formData.company.trim()) {
      return false;
    }

    try {
      setLoading(true);
      await onSubmit(formData);
      handleClose(); // Close modal after successful creation
      return true;
    } catch (error) {
      console.error('Error creating job:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset all modal state
    setActiveTab(0);
    setShowTaskForm(false);
    setEditingTitle(false);
    setEditingCompany(false);
    setAiError(null); // Only clear error, keep analysis cache
    setLoading(false);
    onClose();
  };

  // Format due date for display
  const formatDueDate = (date) => {
    if (!date) return null;
    const taskDate = new Date(date);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (taskDate.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (taskDate.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return taskDate.toLocaleDateString();
    }
  };

  const isOverdue = (date) => {
    if (!date) return false;
    const taskDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    taskDate.setHours(0, 0, 0, 0);
    return taskDate < today;
  };

  // Improved sorting: by due date, then alphabetically for same dates
  const sortedTasks = [...tasks].sort((a, b) => {
    // If both have due dates
    if (a.dueDate && b.dueDate) {
      const dateA = new Date(a.dueDate);
      const dateB = new Date(b.dueDate);
      const dateComparison = dateA - dateB;
      
      // If dates are the same, sort alphabetically
      if (dateComparison === 0) {
        return a.title.localeCompare(b.title);
      }
      return dateComparison;
    }
    // If only one has a due date, prioritize it
    if (a.dueDate && !b.dueDate) return -1;
    if (!a.dueDate && b.dueDate) return 1;
    // If neither has due date, sort alphabetically
    return a.title.localeCompare(b.title);
  });

  // Stage options for Select
  const stageOptions = [
    { value: 'wishlist', label: 'Wishlist' },
    { value: 'applied', label: 'Applied' },
    { value: 'interview', label: 'Interview' },
    { value: 'offer', label: 'Offer' },
    { value: 'rejected', label: 'Rejected' }
  ];

  return (
    <Modal 
      open={open} 
      onClose={handleClose} 
      maxWidth="xl" 
      fullWidth
      fullScreen={window.innerWidth <= 768}
      className="bg-gradient-to-t from-purple-900 via-slate-900 to-slate-950"
      style={{
        minHeight: '80vh',
        maxHeight: '90vh',
        height: 'auto',
        background: 'linear-gradient(to top, rgba(128, 90, 213, 0.2) 20%, rgba(15, 23, 42, 0.95) 60%, rgba(2, 6, 23, 1) 100%)',
        border: '1px solid rgba(229, 231, 235, 0.2)',
        borderRadius: '0.75rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
        backdropFilter: 'blur(16px)'
      }}
    >
      {/* Header */}
      <ModalHeader onClose={handleClose} className="bg-white bg-opacity-10 backdrop-blur-md border-b border-gray-200 border-opacity-20">
        <div className="flex-1">
          {editingTitle ? (
            <div className="relative">
              <input
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleInputChange}
                onBlur={handleTitleBlur}
                onKeyPress={handleTitleKeyPress}
                autoFocus
                placeholder="Enter job title..."
                className="w-full text-4xl font-semibold text-white bg-transparent border-0 rounded-lg px-3 py-2 focus:outline-none transition-all duration-200"
              />
              {savingFields.jobTitle && (
                <div className="absolute right-3 top-3">
                  <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          ) : (
            <H4 
              onClick={handleTitleClick}
              className={`
                cursor-pointer border-2 border-transparent rounded-xl px-4 py-3
                transition-all duration-200 hover:border-white hover:border-opacity-30
                ${formData.jobTitle ? 'text-white' : 'text-gray-400 italic'}
                min-h-[1.5em] flex items-center select-none
              `}
              style={{
                fontSize: '2.25rem', // text-4xl equivalent
                fontWeight: '700', // font-bold equivalent
                lineHeight: '2.5rem'
              }}
              title="Click to edit job title"
            >
              {formData.jobTitle || 'Click to add job title...'}
            </H4>
          )}
          
          {editingCompany ? (
            <div className="relative mt-3">
              <input
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                onBlur={handleCompanyBlur}
                onKeyPress={handleCompanyKeyPress}
                autoFocus
                placeholder="Enter company name"
                className="w-full text-xl font-medium text-gray-300 bg-transparent border-0 rounded-lg px-3 py-2 focus:outline-none transition-all duration-200"
              />
              {savingFields.company && (
                <div className="absolute right-3 top-2">
                  <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          ) : (
            <H6 
              onClick={handleCompanyClick}
              className={`
                cursor-pointer border-2 border-transparent rounded-xl px-4 py-2 mt-3
                transition-all duration-200 hover:border-white hover:border-opacity-30
                ${formData.company ? 'text-gray-300' : 'text-gray-500 italic'}
                min-h-[1.2em] flex items-center select-none
              `}
              style={{
                fontSize: '1.25rem', // text-xl equivalent
                fontWeight: '600', // font-semibold equivalent
                lineHeight: '1.75rem'
              }}
              title="Click to edit company name"
            >
              {formData.company || 'Click to add company...'}
            </H6>
          )}
        </div>
      </ModalHeader>

      {/* Tabs */}
      <div className="border-b border-gray-200 border-opacity-20 bg-white bg-opacity-5 backdrop-blur-xl px-6">
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Details" value={0} />
          <Tab label="Activities" value={1} />
          <Tab label="elena AI ⚡" value={2} />
        </Tabs>
      </div>

      <ModalContent className="bg-transparent text-white flex-1 overflow-hidden flex flex-col" style={{ minHeight: 'calc(80vh - 200px)', maxHeight: 'calc(80vh - 200px)' }}>
        {/* Details Tab */}
        <TabPanel value={0} activeValue={activeTab} className="flex-1 flex flex-col">
          <div className="p-6 flex-1 flex flex-col h-full">
            {/* Top section with form fields */}
            <div className="space-y-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-300 mb-3">Stage</label>
                  <Select
                    name="stage"
                    value={formData.stage}
                    onChange={handleInputChange}
                    options={stageOptions}
                    fullWidth
                    variant="outlined"
                  />
                  {savingFields.stage && (
                    <div className="absolute right-3 top-12">
                      <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-300 mb-3">Location</label>
                  <TextField
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    fullWidth
                    variant="outlined"
                  />
                  {savingFields.location && (
                    <div className="absolute right-3 top-12">
                      <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>

                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-300 mb-3">Job URL</label>
                  <TextField
                    name="jobUrl"
                    value={formData.jobUrl}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    fullWidth
                    type="url"
                    variant="outlined"
                  />
                  {savingFields.jobUrl && (
                    <div className="absolute right-3 top-12">
                      <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Job Description section - fills remaining space */}
            <div className="flex-1 flex flex-col">
              <label className="block text-sm font-semibold text-gray-300 mb-3">
                Job Description
              </label>
              <div className="flex-1 relative">
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Add job description, requirements, responsibilities, or any other notes about this position..."
                  className="w-full h-full bg-white bg-opacity-10 text-white border border-gray-200 border-opacity-20 rounded-lg p-4 resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent placeholder-gray-400"
                  style={{ minHeight: '600px' }}
                />
                {savingFields.notes && (
                  <div className="absolute right-3 top-3 z-10">
                    <div className="w-4 h-4 border-2 border-green-400 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabPanel>

        {/* Activities Tab */}
        <TabPanel value={1} activeValue={activeTab}>
          <div className="p-6">
            {isEditing ? (
              <div>
                {/* Add Task Button - Clean and left-aligned */}
                {!showTaskForm && (
                  <button
                    onClick={handleAddTaskClick}
                    className="flex items-center gap-3 text-gray-400 hover:text-white transition-all duration-200 mb-8 px-4 py-3 rounded-xl hover:bg-white hover:bg-opacity-10 backdrop-blur-sm border border-transparent hover:border-white hover:border-opacity-50 font-medium"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Add a task
                  </button>
                )}

                {/* Add Task Form */}
                {showTaskForm && (
                  <div className="bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-xl p-6 mb-8 backdrop-blur-sm">
                    <div className="space-y-5">
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-3">Task name</label>
                        <TextField
                          value={taskFormData.title}
                          name="title"
                          onChange={handleTaskFormChange}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter' && taskFormData.title.trim()) {
                              handleCreateTask();
                            }
                          }}
                          fullWidth
                          size="small"
                          autoFocus
                          variant="outlined"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-semibold text-gray-300 mb-3">Due date</label>
                        <div className="relative">
                          {editingFormDate ? (
                            <div 
                              ref={formCalendarRef}
                              className="absolute z-50 left-0 top-0 w-80 bg-[#1a102b] rounded-xl shadow-2xl p-3 border border-purple-900" 
                              style={{ color: 'white' }}
                            >
                              {showMonthPicker ? (
                                <MonthPicker
                                  year={selectedYear}
                                  onMonthSelect={handleMonthSelect}
                                  onYearChange={setSelectedYear}
                                />
                              ) : (
                                <div>
                                  <DayPicker
                                    mode="single"
                                    selected={taskFormData.dueDate ? new Date(taskFormData.dueDate) : undefined}
                                    month={new Date(selectedYear, selectedMonth)}
                                    onSelect={(date) => {
                                      if (date) {
                                        const year = date.getFullYear();
                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                        const day = String(date.getDate()).padStart(2, '0');
                                        setTaskFormData(prev => ({
                                          ...prev,
                                          dueDate: `${year}-${month}-${day}`
                                        }));
                                        setEditingFormDate(false);
                                      }
                                    }}
                                    modifiersClassNames={{
                                      selected: 'bg-purple-600 text-white rounded-full',
                                      today: 'border border-purple-400 text-white',
                                    }}
                                    className="!bg-transparent text-white"
                                    showOutsideDays={false}
                                    onMonthChange={(month) => {
                                      setSelectedMonth(month.getMonth());
                                      setSelectedYear(month.getFullYear());
                                    }}
                                    style={{ 
                                      '--rdp-cell-size': '35px',
                                      '--rdp-accent-color': '#a855f7',
                                      color: 'white'
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setEditingFormDate(true)}
                              className="w-48 px-3 py-2 text-left bg-white bg-opacity-10 border border-purple-400 rounded text-white text-sm hover:bg-opacity-15 transition-colors focus:outline-none focus:border-purple-500"
                            >
                              {taskFormData.dueDate ? 
                                new Date(taskFormData.dueDate).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric',
                                  year: 'numeric'
                                }) : 
                                'Select due date'
                              }
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                      <Button 
                        variant="text"
                        color="secondary"
                        onClick={handleCancelTask}
                        size="small"
                        disabled={taskCreating}
                        className="px-4 py-2"
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="contained"
                        color="primary"
                        onClick={handleCreateTask}
                        size="small"
                        disabled={!taskFormData.title.trim() || taskCreating}
                        className="px-6 py-2 bg-purple-600 hover:bg-purple-700"
                      >
                        {taskCreating ? 'Adding...' : 'Add task'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Tasks List */}
                {tasksLoading ? (
                  <div className="text-center py-4 text-gray-300">
                    Loading tasks...
                  </div>
                ) : sortedTasks.length > 0 ? (
                  <div className="space-y-3">
                    {sortedTasks.map((task) => (
                      <div key={task._id} className="group flex items-start gap-4 p-4 bg-white bg-opacity-5 rounded-xl hover:bg-white hover:bg-opacity-10 transition-all duration-200 border border-gray-200 border-opacity-20 hover:border-opacity-30 backdrop-blur-sm">
                        {/* Checkbox */}
                        <button
                          onClick={() => handleToggleTask(task._id, !task.completed)}
                          className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                            task.completed 
                              ? 'bg-green-500 border-green-500' 
                              : 'border-gray-300 border-opacity-50 hover:border-purple-400'
                          }`}
                        >
                          {task.completed && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>

                        {/* Task Content - Inline Editing */}
                        {editingTaskId === task._id ? (
                          <div className="flex-1 space-y-3">
                            <div>
                              <label className="block text-xs font-medium text-purple-300 mb-1">Task name</label>
                              <TextField
                                value={editingTaskData.title}
                                name="title"
                                onChange={handleInlineEditChange}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveTaskEdit();
                                  } else if (e.key === 'Escape') {
                                    handleCancelTaskEdit();
                                  }
                                }}
                                size="small"
                                fullWidth
                                autoFocus
                                variant="outlined"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-purple-300 mb-1">Due date</label>
                              <div className="relative">
                                {editingInlineDate ? (
                                  <div 
                                    ref={inlineCalendarRef}
                                    className="absolute z-50 left-0 top-0 w-80 bg-[#1a102b] rounded-xl shadow-2xl p-3 border border-purple-900" 
                                    style={{ color: 'white' }}
                                  >
                                    {showMonthPicker ? (
                                      <MonthPicker
                                        year={selectedYear}
                                        onMonthSelect={handleMonthSelect}
                                        onYearChange={setSelectedYear}
                                      />
                                    ) : (
                                      <div>
                                        <DayPicker
                                          mode="single"
                                          selected={editingTaskData.dueDate ? new Date(editingTaskData.dueDate) : undefined}
                                          month={new Date(selectedYear, selectedMonth)}
                                          onSelect={(date) => {
                                            if (date) {
                                              const year = date.getFullYear();
                                              const month = String(date.getMonth() + 1).padStart(2, '0');
                                              const day = String(date.getDate()).padStart(2, '0');
                                              setEditingTaskData(prev => ({
                                                ...prev,
                                                dueDate: `${year}-${month}-${day}`
                                              }));
                                              setEditingInlineDate(false);
                                            }
                                          }}
                                          modifiersClassNames={{
                                            selected: 'bg-purple-600 text-white rounded-full',
                                            today: 'border border-purple-400 text-white',
                                          }}
                                          className="!bg-transparent text-white"
                                          showOutsideDays={false}
                                          onMonthChange={(month) => {
                                            setSelectedMonth(month.getMonth());
                                            setSelectedYear(month.getFullYear());
                                          }}
                                          style={{ 
                                            '--rdp-cell-size': '35px',
                                            '--rdp-accent-color': '#a855f7',
                                            color: 'white'
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => setEditingInlineDate(true)}
                                    className="w-48 px-3 py-2 text-left bg-white bg-opacity-10 border border-purple-400 rounded text-white text-sm hover:bg-opacity-15 transition-colors focus:outline-none focus:border-purple-500"
                                  >
                                    {editingTaskData.dueDate ? 
                                      new Date(editingTaskData.dueDate).toLocaleDateString('en-US', { 
                                        month: 'short', 
                                        day: 'numeric',
                                        year: 'numeric'
                                      }) : 
                                      'Select due date'
                                    }
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <IconButton 
                                size="small" 
                                onClick={handleSaveTaskEdit}
                                className="text-green-400 hover:text-green-300"
                              >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </IconButton>
                              <IconButton 
                                size="small" 
                                onClick={handleCancelTaskEdit}
                                className="text-gray-400 hover:text-gray-300"
                              >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                              </IconButton>
                            </div>
                          </div>
                        ) : (
                          <>
                            {/* Task Content */}
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm font-medium ${task.completed ? 'line-through text-gray-400' : 'text-white'}`}>
                                {task.title}
                              </div>
                              {task.dueDate && (
                                <>
                                  {editingDateTaskId === task._id ? (
                                    <div className="mt-2 relative">
                                      <div 
                                        ref={calendarRef}
                                        className="absolute z-50 left-0 top-0 w-80 bg-[#1a102b] rounded-xl shadow-2xl p-3 border border-purple-900" 
                                        style={{ color: 'white' }}
                                      >
                                        {showMonthPicker ? (
                                          <MonthPicker
                                            year={selectedYear}
                                            onMonthSelect={handleMonthSelect}
                                            onYearChange={setSelectedYear}
                                          />
                                        ) : (
                                          <div>
                                            <DayPicker
                                              mode="single"
                                              selected={task.dueDate ? new Date(task.dueDate) : undefined}
                                              month={new Date(selectedYear, selectedMonth)}
                                              onSelect={(date) => {
                                                if (date) {
                                                  const year = date.getFullYear();
                                                  const month = String(date.getMonth() + 1).padStart(2, '0');
                                                  const day = String(date.getDate()).padStart(2, '0');
                                                  handleDateChange(task._id, `${year}-${month}-${day}`);
                                                }
                                              }}
                                              modifiersClassNames={{
                                                selected: 'bg-purple-600 text-white rounded-full',
                                                today: 'border border-purple-400 text-white',
                                              }}
                                              className="!bg-transparent text-white"
                                              showOutsideDays={false}
                                              onMonthChange={(month) => {
                                                setSelectedMonth(month.getMonth());
                                                setSelectedYear(month.getFullYear());
                                              }}
                                              style={{ 
                                                '--rdp-cell-size': '35px',
                                                '--rdp-accent-color': '#a855f7',
                                                color: 'white'
                                              }}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => handleDateChipClick(task)}
                                      className={`text-xs mt-1 flex items-center gap-1 px-2 py-1 rounded border transition-colors ${
                                        isOverdue(task.dueDate) && !task.completed 
                                          ? 'text-red-300 border-red-400 hover:bg-red-400 hover:bg-opacity-10' 
                                          : 'text-purple-300 border-purple-400 hover:bg-purple-400 hover:bg-opacity-10'
                                      }`}
                                    >
                                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                      </svg>
                                      {formatDueDate(task.dueDate)}
                                    </button>
                                  )}
                                </>
                              )}
                            </div>

                            {/* Task Menu */}
                            <div className="relative">
                              <IconButton
                                size="small"
                                onClick={() => setTaskMenuOpen(taskMenuOpen === task._id ? null : task._id)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-gray-200"
                              >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                                </svg>
                              </IconButton>
                              
                              {/* Dropdown Menu */}
                              {taskMenuOpen === task._id && (
                                <div className="absolute right-0 top-8 w-32 bg-white bg-opacity-95 border border-gray-300 border-opacity-30 rounded-lg shadow-lg z-10 backdrop-blur-sm">
                                  <button
                                    onClick={() => handleEditTask(task)}
                                    className="w-full px-3 py-2 text-left text-gray-800 hover:bg-gray-100 rounded-t-lg flex items-center gap-2 text-sm"
                                  >
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                    </svg>
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteTask(task._id)}
                                    className="w-full px-3 py-2 text-left text-red-600 hover:bg-red-50 rounded-b-lg flex items-center gap-2 text-sm"
                                  >
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9zM4 5a2 2 0 012-2h8a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zM8 8a1 1 0 012 0v3a1 1 0 11-2 0V8zm4 0a1 1 0 112 0v3a1 1 0 11-2 0V8z" clipRule="evenodd" />
                                    </svg>
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  !showTaskForm && (
                                    <div className="text-center py-12 text-gray-400">
                  <div className="w-16 h-16 mx-auto mb-4 bg-white bg-opacity-5 rounded-full flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <p className="text-lg font-medium mb-2">No tasks yet</p>
                  <p className="text-sm">Add your first task to get started!</p>
                </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-20 h-20 mx-auto mb-6 bg-white bg-opacity-5 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <H6 className="text-gray-300 text-xl font-semibold mb-3">Save this job to manage tasks</H6>
                <p className="text-gray-400 max-w-md mx-auto leading-relaxed">
                  Task management will be available after creating the job application.
                </p>
              </div>
            )}
          </div>
        </TabPanel>

        {/* AI Tab */}
        <TabPanel value={2} activeValue={activeTab}>
          <div className="p-6">
            {isEditing ? (
              <div>
                                        <div className="mb-6">
                  <h3 className="text-xl font-semibold text-white mb-2">CV Optimization</h3>
                  <p className="text-gray-300">Elena AI will recommend your best base CV and suggest specific optimizations to maximize your fit for this role.</p>
                </div>

                {aiLoading ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 bg-white bg-opacity-5 rounded-full flex items-center justify-center">
                      <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                    <p className="text-white font-medium">Analyzing your CVs...</p>
                    <p className="text-gray-400 text-sm mt-1">This may take a few seconds</p>
                  </div>
                ) : aiError ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 bg-red-500 bg-opacity-20 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <p className="text-red-300 font-medium mb-2">Analysis Failed</p>
                    <p className="text-gray-400 text-sm mb-4">{aiError}</p>
                    <button
                      onClick={handleAIAnalysis}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                ) : aiAnalysis ? (
                  <div>
                    {aiAnalysis.recommendedCV ? (
                      <div className="bg-white bg-opacity-10 rounded-xl p-6 border border-purple-400">
                        {aiAnalysis.isDemoMode && (
                          <div className="mb-4 p-3 bg-orange-900 bg-opacity-50 border border-orange-500 rounded-lg">
                            <div className="flex items-center gap-2 text-orange-200 text-sm">
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              Demo Mode: OpenAI quota exceeded - showing intelligent fallback analysis
                            </div>
                          </div>
                        )}
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-lg font-semibold text-white">Best Base CV</h4>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={handleAIAnalysis}
                              className="px-3 py-1 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Re-analyze
                            </button>
                            <div className="flex items-center gap-2">
                              <div className="text-2xl font-bold text-purple-400">
                                {aiAnalysis.recommendedCV.matchScore}%
                              </div>
                              <div className="text-sm text-gray-400">match</div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <h5 className="text-lg font-medium text-white mb-2">{aiAnalysis.recommendedCV.cvLabel}</h5>
                          <p className="text-gray-300 text-sm">{aiAnalysis.recommendedCV.recommendation}</p>
                        </div>

                        {aiAnalysis.recommendedCV.gaps && aiAnalysis.recommendedCV.gaps.length > 0 && (
                          <div>
                            <h6 className="text-blue-400 font-medium mb-2">📝 Suggested Changes</h6>
                            <ul className="space-y-2">
                              {aiAnalysis.recommendedCV.gaps.map((gap, index) => (
                                <li key={index} className="text-gray-300 text-sm flex items-start gap-2 p-2 bg-gray-800 bg-opacity-50 rounded">
                                  <span className="text-blue-400 mt-0.5 font-bold">{index + 1}.</span>
                                  <span className="leading-relaxed">{gap}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-12 text-gray-400">
                        <div className="w-16 h-16 mx-auto mb-4 bg-white bg-opacity-5 rounded-full flex items-center justify-center">
                          <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <p className="text-lg font-medium mb-2">No CVs available for analysis</p>
                        <p className="text-sm">Upload CVs in the Files section to get AI recommendations.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 bg-white bg-opacity-5 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    </div>
                    <p className="text-white font-medium mb-2">Ready for AI Analysis</p>
                    <button
                      onClick={handleAIAnalysis}
                      className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                    >
                      Analyze CVs
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-20 h-20 mx-auto mb-6 bg-white bg-opacity-5 rounded-full flex items-center justify-center">
                  <svg className="w-10 h-10 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <H6 className="text-gray-300 text-xl font-semibold mb-3">Save this job to get AI insights</H6>
                <p className="text-gray-400 max-w-md mx-auto leading-relaxed">
                  Elena AI will analyze your CVs against this job description after you create the application.
                </p>
              </div>
            )}
          </div>
        </TabPanel>
      </ModalContent>

      {/* Actions for new jobs */}
      {!isEditing && (
        <ModalActions className="bg-white bg-opacity-5 backdrop-blur-xl border-t border-gray-200 border-opacity-20">
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={handleClose} 
            disabled={loading}
            className="px-6 py-2 border-gray-600 text-gray-300 hover:border-gray-500 hover:text-white"
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            color="primary"
            onClick={handleCreateJob}
            disabled={loading || !formData.jobTitle.trim() || !formData.company.trim()}
            className="px-8 py-2 bg-purple-600 hover:bg-purple-700 font-semibold"
          >
            {loading ? 'Creating...' : 'Create Job'}
          </Button>
        </ModalActions>
      )}
    </Modal>
  );
}

export default JobDetailsModal;